import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ImmutablePureComponent from 'react-immutable-pure-component'
import { Map as ImmutableMap } from 'immutable'
import { createSelector } from 'reselect'
import { supportsPassiveEvents } from 'detect-it'
import { changeSetting } from '../../actions/settings'
import { useEmoji } from '../../actions/emojis'
import { closePopover } from '../../actions/popover'
import { buildCustomEmojis } from '../emoji/emoji'
import PopoverLayout from './popover_layout'
import Picker from 'emoji-mart/dist-es/components/picker/picker'
import Emoji from 'emoji-mart/dist-es/components/emoji/emoji'
import { dispatchWindowEvent } from '../../utils/events'

import '!style-loader!css-loader!emoji-mart/css/emoji-mart.css'

const assetHost = process.env.CDN_HOST || ''

const backgroundImageFn = () => `${assetHost}/emoji/sheet_32.png`
const listenerOptions = supportsPassiveEvents ? { passive: true } : false

const perLine = 8
const lines = 2

const DEFAULTS = [
  '+1',
  'grinning',
  'kissing_heart',
  'heart_eyes',
  'laughing',
  'stuck_out_tongue_winking_eye',
  'sweat_smile',
  'joy',
  'yum',
  'disappointed',
  'thinking_face',
  'weary',
  'sob',
  'sunglasses',
  'heart',
  'ok_hand'
]

const categoriesSort = [
  'recent',
  'custom',
  'people',
  'nature',
  'foods',
  'activity',
  'places',
  'objects',
  'symbols',
  'flags'
]

const getFrequentlyUsedEmojis = createSelector(
  [state => state.getIn(['settings', 'frequentlyUsedEmojis'], ImmutableMap())],
  emojiCounters => {
    let emojis = emojiCounters
      .keySeq()
      .sort((a, b) => emojiCounters.get(a) - emojiCounters.get(b))
      .reverse()
      .slice(0, perLine * lines)
      .toArray()

    if (emojis.length < DEFAULTS.length) {
      let uniqueDefaults = DEFAULTS.filter(emoji => !emojis.includes(emoji))
      emojis = emojis.concat(
        uniqueDefaults.slice(0, DEFAULTS.length - emojis.length)
      )
    }

    return emojis
  }
)

const getCustomEmojis = createSelector(
  [state => state.get('custom_emojis')],
  emojis =>
    emojis
      .filter(e => e.get('visible_in_picker'))
      .sort((a, b) => {
        const aShort = a.get('shortcode').toLowerCase()
        const bShort = b.get('shortcode').toLowerCase()

        if (aShort < bShort) {
          return -1
        } else if (aShort > bShort) {
          return 1
        }

        return 0
      })
)

class EmojiPickerMenu extends ImmutablePureComponent {
  getI18n = () => {
    return {
      search: 'Search for emoji',
      notfound: 'No emojos!! (╯°□°）╯︵ ┻━┻',
      categories: {
        search: 'Search results',
        recent: 'Frequently used',
        people: 'People',
        nature: 'Nature',
        foods: 'Food & Drink',
        activity: 'Activity',
        places: 'Travel & Places',
        objects: 'Objects',
        symbols: 'Symbols',
        flags: 'Flags',
        custom: 'Custom',
      }
    }
  }

  handleClick = (emoji, evt) => {
    if (!emoji.native) {
      emoji.native = emoji.colons
    }

    this.props.onPick(emoji)

    // emoji madness, press mod and keep clicking emojis 🤓
    const keepOpen = evt && (evt.shiftKey || evt.metaKey || evt.ctrlKey)

    if (!keepOpen) {
      this.props.onClose()
    }
  }

  handleModifierChange = modifier => {
    this.props.onSkinTone(modifier)
  }

  render() {
    const { customEmojis, skinTone, frequentlyUsedEmojis } = this.props

    const title = 'Insert emoji'

    return (
      <Picker
        backgroundImageFn={backgroundImageFn}
        custom={buildCustomEmojis(customEmojis)}
        title={title}
        i18n={this.getI18n()}
        onClick={this.handleClick}
        include={categoriesSort}
        recent={frequentlyUsedEmojis}
        skin={skinTone}
        perLine={8}
        emojiSize={29}
        sheetSize={32}
        set="twitter"
        color="#30CE7D"
        emoji=""
        autoFocus
        emojiTooltip
        onSkinChange={this.handleModifierChange}
      />
    )
  }
}

EmojiPickerMenu.propTypes = {
  customEmojis: ImmutablePropTypes.list,
  frequentlyUsedEmojis: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  arrowOffsetLeft: PropTypes.string,
  arrowOffsetTop: PropTypes.string,
  skinTone: PropTypes.number.isRequired,
  onSkinTone: PropTypes.func.isRequired,
}

EmojiPickerMenu.defaultProps = { frequentlyUsedEmojis: [] }

class EmojiPickerPopover extends ImmutablePureComponent {
  onHideDropdown = () => {
    this.props.onClosePopover()
  }

  onPick = emoji => {
    this.props.onPickEmoji(emoji) // to redux
    // this.props.onEmoji(emoji) // to ComposeForm
    const { composerId } = this.props
    const text = emoji.native || emoji.colons
    dispatchWindowEvent('composer-insert', { composerId, text })
  }

  render() {
    const {
      onSkinTone,
      skinTone,
      frequentlyUsedEmojis,
      customEmojis,
      isXS
    } = this.props

    return (
      <PopoverLayout width={340} isXS={isXS} onClose={this.onHideDropdown}>
        <EmojiPickerMenu
          customEmojis={customEmojis}
          onClose={this.onHideDropdown}
          onPick={this.onPick}
          onSkinTone={onSkinTone}
          skinTone={skinTone}
          frequentlyUsedEmojis={frequentlyUsedEmojis}
        />
      </PopoverLayout>
    )
  }
}

const mapStateToProps = state => ({
  customEmojis: getCustomEmojis(state),
  skinTone: state.getIn(['settings', 'skinTone']),
  frequentlyUsedEmojis: getFrequentlyUsedEmojis(state)
})

const mapDispatchToProps = dispatch => ({
  onClosePopover: () => dispatch(closePopover()),
  onSkinTone: skinTone => dispatch(changeSetting(['skinTone'], skinTone)),
  onPickEmoji: emoji => dispatch(useEmoji(emoji))
})

EmojiPickerPopover.propTypes = {
  customEmojis: ImmutablePropTypes.list,
  frequentlyUsedEmojis: PropTypes.arrayOf(PropTypes.string),
  onPickEmoji: PropTypes.func.isRequired,
  onSkinTone: PropTypes.func.isRequired,
  skinTone: PropTypes.number.isRequired,
  onClosePopover: PropTypes.func.isRequired,
  isXS: PropTypes.bool,
}

export default connect(mapStateToProps, mapDispatchToProps)(EmojiPickerPopover)
